// Paste-JSON backdoor — manual route for principals/dev who don't have an
// LLM API key configured but already have a schema-conforming JSON (e.g. from
// a Claude.ai chat session).
//
// Decision (Session 19F):
//   - Inline (not modal) — keeps the user in the same page, lower cognitive load.
//   - Validates via the canonical IngestPackageSchema; same shape Claude worker
//     produces, so cached results from either path are interchangeable.
//   - On success, builds a synthetic IngestResult wrapper with providerName='paste'
//     and zero token meta — downstream code (ResultView) doesn't care where the
//     package came from.
//   - Gated by `enabled` prop. The composing CourseView feature-flags this with
//     `PASTE_JSON_ENABLED` so we can hide it later without restructuring this
//     component.

import { useState } from 'react';
import { IngestPackageSchema, migrateLegacyIngestPackage, type IngestPackage } from '@examforge/shared/schemas';
import { useT } from '../i18n/index.js';
import type { IngestResult } from '../api/ingest.js';

export interface PasteJsonInputProps {
  /** Called when the pasted JSON validates. Caller decides what to do (cache + transition). */
  onAccept(result: IngestResult): void | Promise<void>;
  onCancel(): void;
}

export function PasteJsonInput({ onAccept, onCancel }: PasteJsonInputProps) {
  const t = useT();
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [migrationNote, setMigrationNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function validateAndSave(): Promise<void> {
    setError(null);
    setMigrationNote(null);
    setBusy(true);
    try {
      // Three-phase: JSON parse → forward-migrate legacy fields → Zod parse.
      // Legacy migration (Session 19AP) handles 19AE schema collapse so old
      // pastes (e.g. v1 IB JSONs with `title_en`, `subSection.label`) don't
      // dead-end at safeParse. Idempotent on already-current shapes.
      let raw: unknown;
      try {
        raw = JSON.parse(text);
      } catch (e) {
        setError(`${t('course.pasteJsonInvalid')}: ${(e as Error).message}`);
        return;
      }
      const { migrated, applied } = migrateLegacyIngestPackage(raw);
      if (applied.length > 0) {
        setMigrationNote(applied.join(', '));
      }
      const parsed = IngestPackageSchema.safeParse(migrated);
      if (!parsed.success) {
        const issues = parsed.error.errors
          .slice(0, 8)
          .map((e) => `  ${e.path.join('.') || '<root>'}: ${e.message}`)
          .join('\n');
        const overflow = parsed.error.errors.length > 8 ? `\n  …+${parsed.error.errors.length - 8} more` : '';
        setError(`${t('course.pasteJsonValidationFailed')}:\n${issues}${overflow}`);
        return;
      }
      const synthetic: IngestResult = {
        ok: true,
        package: parsed.data as IngestPackage,
        meta: {
          inputTokens: 0,
          outputTokens: 0,
          durationMs: 0,
          providerName: 'paste',
        },
      };
      await onAccept(synthetic);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="paste-json-section">
      <h2>{t('course.pasteJsonHeading')}</h2>
      <p className="paste-json-section-hint">{t('course.pasteJsonHint')}</p>
      <p className="paste-json-section-schema">{t('course.pasteJsonHintSchema')}</p>

      <textarea
        className="paste-json-section-textarea"
        value={text}
        placeholder={t('course.pasteJsonPlaceholder')}
        onChange={(e) => {
          setText(e.target.value);
          if (error) setError(null);
          if (migrationNote) setMigrationNote(null);
        }}
        spellCheck={false}
        rows={16}
      />

      {migrationNote && (
        <p className="paste-json-section-migration-note" style={{ opacity: 0.75, fontSize: '0.85rem', marginTop: '0.5rem' }}>
          {t('course.pasteJsonLegacyMigrated', { rules: migrationNote })}
        </p>
      )}
      {error && <pre className="paste-json-section-error">{error}</pre>}

      <div className="paste-json-section-buttons">
        <button type="button" className="btn-secondary" onClick={onCancel} disabled={busy}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={validateAndSave}
          disabled={busy || text.trim().length === 0}
        >
          {t('course.pasteJsonValidateAndSave')}
        </button>
      </div>
    </section>
  );
}
